import { type RefObject, useEffect, useRef } from 'react';

export type KeyboardShortcutOptions = {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  preventDefault?: boolean;
};

type KeyboardShortcutHandler = (() => void) | RefObject<HTMLInputElement>;

export const useKeyboardShortcut = (
  handler: KeyboardShortcutHandler,
  options: KeyboardShortcutOptions
) => {
  const {
    key,
    ctrlKey,
    metaKey,
    shiftKey,
    altKey,
    preventDefault = true
  } = options;

  const handlerRef = useRef(handler);
  const isRef = 'current' in handler;

  useEffect(() => {
    if (!isRef) {
      handlerRef.current = handler;
    }
  }, [handler, isRef]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === key &&
        (ctrlKey === undefined || e.ctrlKey === ctrlKey) &&
        (metaKey === undefined || e.metaKey === metaKey) &&
        (shiftKey === undefined || e.shiftKey === shiftKey) &&
        (altKey === undefined || e.altKey === altKey)
      ) {
        if (preventDefault) e.preventDefault();

        if (isRef) {
          (handler as RefObject<HTMLInputElement>).current?.focus();
        } else {
          (handler as () => void)();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [key, ctrlKey, metaKey, shiftKey, altKey, preventDefault, isRef]);
};
